import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ApiService } from "../../services/api";
import { SOCIAL_STORAGE_KEY } from "./socialShared";

const SocialModuleContext = createContext(undefined);

export function SocialModuleProvider({ children }) {
  const [providers, setProviders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeBrandId, setActiveBrandIdState] = useState(() => {
    try {
      return localStorage.getItem(SOCIAL_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  const refreshProviders = useCallback(async () => {
    const data = await ApiService.getSocialProviders();
    setProviders(Array.isArray(data) ? data : []);
    return data;
  }, []);

  const refreshBrands = useCallback(async () => {
    const data = await ApiService.getSocialBrands();
    setBrands(Array.isArray(data) ? data : []);
    return data;
  }, []);

  const refreshAll = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");
      await Promise.all([refreshProviders(), refreshBrands()]);
    } catch (err) {
      setError(err?.message || "Sosyal medya modülü yüklenemedi.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [refreshBrands, refreshProviders]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!brands.length) {
      setActiveBrandIdState("");
      try {
        localStorage.removeItem(SOCIAL_STORAGE_KEY);
      } catch {}
      return;
    }

    const hasActiveBrand = brands.some((item) => item.id === activeBrandId);
    if (!hasActiveBrand) {
      const nextBrandId = brands[0]?.id || "";
      setActiveBrandIdState(nextBrandId);
      try {
        localStorage.setItem(SOCIAL_STORAGE_KEY, nextBrandId);
      } catch {}
    }
  }, [activeBrandId, brands]);

  const setActiveBrandId = useCallback((brandId) => {
    setActiveBrandIdState(brandId);
    try {
      if (brandId) {
        localStorage.setItem(SOCIAL_STORAGE_KEY, brandId);
      } else {
        localStorage.removeItem(SOCIAL_STORAGE_KEY);
      }
    } catch {}
  }, []);

  const activeBrand = useMemo(
    () => brands.find((item) => item.id === activeBrandId) || null,
    [activeBrandId, brands]
  );

  const connectedProviders = useMemo(
    () => providers.filter((item) => item.isConnected),
    [providers]
  );

  const value = useMemo(
    () => ({
      providers,
      connectedProviders,
      brands,
      activeBrand,
      activeBrandId,
      setActiveBrandId,
      loading,
      refreshing,
      error,
      refreshProviders,
      refreshBrands,
      refreshAll,
      setProviders,
      setBrands,
    }),
    [
      activeBrand,
      activeBrandId,
      brands,
      connectedProviders,
      error,
      loading,
      providers,
      refreshAll,
      refreshBrands,
      refreshProviders,
      refreshing,
      setActiveBrandId,
    ]
  );

  return (
    <SocialModuleContext.Provider value={value}>
      {children}
    </SocialModuleContext.Provider>
  );
}

export function useSocialModule() {
  const context = useContext(SocialModuleContext);
  if (!context) {
    throw new Error("useSocialModule must be used within SocialModuleProvider");
  }
  return context;
}
